import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Mail,
  Inbox,
  CheckCircle2,
  Clock,
  AlertCircle,
  RefreshCw,
  Send,
  Sparkles,
  Plus,
  Trash2,
  ExternalLink,
  ListChecks,
  MessageSquare,
  Wand2,
  Copy,
  Flame,
  Building2,
  CalendarClock,
  Receipt,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { MarkdownView } from '../components/MarkdownView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRIORITY_STYLES = {
  high: 'bg-red-100 text-red-800 border-red-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  low: 'bg-emerald-100 text-emerald-800 border-emerald-200',
};
const STATUS_STYLES = {
  open: 'bg-sky-100 text-sky-800 border-sky-200',
  in_progress: 'bg-violet-100 text-violet-800 border-violet-200',
  closed: 'bg-zinc-200 text-zinc-700 border-zinc-300',
};

const QUICK_PROMPTS = [
  {
    icon: Flame,
    label: 'High priority',
    prompt:
      "List today's high-priority open and in-progress tasks across all connected accounts as a markdown table with columns: Account, Title, Due, Owner.",
  },
  {
    icon: Building2,
    label: 'Vendor follow-ups',
    prompt:
      'Show all open tasks and recent threads in the vendor_query or follow_up category. Group by vendor in a markdown table with columns: Vendor, Subject, Last activity, Status.',
  },
  {
    icon: Receipt,
    label: 'Awaiting payments',
    prompt:
      'Which payment-related items are awaiting action (open or in-progress)? List as a markdown table with columns: Vendor / Counterparty, Amount (₹), Due, Source thread.',
  },
  {
    icon: CalendarClock,
    label: 'Last 7 days',
    prompt:
      'Summarise activity from the last 7 days. Give a 1-line direct headline, then a markdown table of the top 8 threads (columns: Subject, Account, Category, Last activity).',
  },
  {
    icon: AlertTriangle,
    label: 'Needs my attention',
    prompt:
      "What needs my attention right now? Combine high-priority open tasks and threads marked 'approval_needed'. Return a markdown table with columns: Type (Task/Thread), Title, Why it matters, Suggested next step.",
  },
];

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const EmailHub = () => {
  const { user } = useAuth();
  const isAdmin = !!(user?.is_super_admin || user?.role === 'admin');
  const [searchParams, setSearchParams] = useSearchParams();

  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [threads, setThreads] = useState([]);
  const [statusFilter, setStatusFilter] = useState('open');
  const [accountFilter, setAccountFilter] = useState('all');
  const [syncing, setSyncing] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [connectLabel, setConnectLabel] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [activeTab, setActiveTab] = useState('tasks');

  // Thread detail
  const [openThread, setOpenThread] = useState(null);
  const [openThreadDetail, setOpenThreadDetail] = useState(null);

  // Compose AI reply state (per open thread)
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeTone, setComposeTone] = useState('professional');
  const [composeInstructions, setComposeInstructions] = useState('');
  const [composeLoading, setComposeLoading] = useState(false);
  const [composeDraft, setComposeDraft] = useState(null);

  // Chat state (session-only)
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [chatModel, setChatModel] = useState('');
  const chatBottomRef = useRef(null);

  // Manual "Add Open Issue" dialog state
  const [addIssueOpen, setAddIssueOpen] = useState(false);
  const [newIssue, setNewIssue] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_hint: '',
    assignee_user_id: 'unassigned',
    account_id: 'none',
  });
  const [addIssueSaving, setAddIssueSaving] = useState(false);

  const resetNewIssue = () =>
    setNewIssue({
      title: '',
      description: '',
      priority: 'medium',
      due_hint: '',
      assignee_user_id: 'unassigned',
      account_id: 'none',
    });

  const handleCreateIssue = async () => {
    const title = newIssue.title.trim();
    if (!title) {
      toast.error('Title is required');
      return;
    }
    setAddIssueSaving(true);
    try {
      await axios.post(
        `${API}/gmail/tasks`,
        {
          title,
          description: newIssue.description.trim(),
          priority: newIssue.priority,
          due_hint: newIssue.due_hint.trim() || null,
          assignee_user_id:
            newIssue.assignee_user_id && newIssue.assignee_user_id !== 'unassigned'
              ? newIssue.assignee_user_id
              : null,
          account_id:
            newIssue.account_id && newIssue.account_id !== 'none' ? newIssue.account_id : null,
        },
        { withCredentials: true }
      );
      toast.success('Open issue added');
      setAddIssueOpen(false);
      resetNewIssue();
      fetchTasks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add open issue');
    } finally {
      setAddIssueSaving(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`${API}/gmail/accounts`, { withCredentials: true });
      setAccounts(res.data || []);
    } catch (e) {
      // 403 is expected for non-admins; log anything else.
      if (e?.response?.status !== 401 && e?.response?.status !== 403) {
        console.warn('EmailHub: gmail accounts fetch failed:', e?.response?.data || e?.message);
      }
    }
  };
  const fetchUsers = async () => {
    try {
      // Use /users/assignable so non-super-admins can also see the assignee
      // list. Returns minimal {id, name, email}.
      const res = await axios.get(`${API}/users/assignable`, { withCredentials: true });
      const list = Array.isArray(res.data?.users) ? res.data.users : [];
      setUsers(list);
    } catch {
      setUsers([]);
    }
  };
  const fetchTasks = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (accountFilter && accountFilter !== 'all') params.append('account_id', accountFilter);
      const url = `${API}/gmail/tasks${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await axios.get(url, { withCredentials: true });
      setTasks(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load tasks');
    }
  };
  const fetchThreads = async () => {
    try {
      const params = new URLSearchParams();
      if (accountFilter && accountFilter !== 'all') params.append('account_id', accountFilter);
      const url = `${API}/gmail/threads${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await axios.get(url, { withCredentials: true });
      setThreads(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load threads');
    }
  };

  useEffect(() => {
    fetchAccounts();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTasks();
    fetchThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, accountFilter]);

  // Toast on successful Gmail OAuth callback redirect
  useEffect(() => {
    const connected = searchParams.get('gmail_connected');
    const errMsg = searchParams.get('gmail_error');
    if (connected) {
      toast.success(`Connected ${connected}`);
      const next = new URLSearchParams(searchParams);
      next.delete('gmail_connected');
      setSearchParams(next, { replace: true });
      fetchAccounts();
    }
    if (errMsg) {
      toast.error(`Gmail connection failed: ${errMsg}`);
      const next = new URLSearchParams(searchParams);
      next.delete('gmail_error');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll the chat
  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const handleConnect = async () => {
    if (!isAdmin) return;
    setConnecting(true);
    try {
      const params = new URLSearchParams();
      if (connectLabel.trim()) params.append('label', connectLabel.trim());
      const res = await axios.get(
        `${API}/gmail/oauth/connect${params.toString() ? `?${params.toString()}` : ''}`,
        { withCredentials: true }
      );
      window.location.href = res.data.auth_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start OAuth flow');
      setConnecting(false);
    }
  };

  const handleDisconnect = async (accountId, email) => {
    if (!window.confirm(`Disconnect ${email}? This deletes all cached emails and tasks for this account.`)) return;
    try {
      await axios.delete(`${API}/gmail/accounts/${accountId}`, { withCredentials: true });
      toast.success(`${email} disconnected`);
      await fetchAccounts();
      await fetchTasks();
      await fetchThreads();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to disconnect');
    }
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/gmail/sync-all`, {}, { withCredentials: true });
      const summary = (res.data?.results || []).map((r) =>
        r.error ? `${r.account}: ${r.error}` : `${r.account}: +${r.new_messages} msgs`
      );
      toast.success(`Sync complete\n${summary.join('\n')}`);
      await fetchAccounts();
      await fetchTasks();
      await fetchThreads();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleTaskUpdate = async (task, patch) => {
    try {
      await axios.patch(`${API}/gmail/tasks/${task.id}`, patch, { withCredentials: true });
      await fetchTasks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const openThreadFor = async (threadId) => {
    setOpenThread({ thread_id: threadId });
    setOpenThreadDetail(null);
    setComposeDraft(null);
    setComposeOpen(false);
    setComposeInstructions('');
    setComposeTone('professional');
    try {
      const res = await axios.get(`${API}/gmail/threads/${threadId}`, { withCredentials: true });
      setOpenThreadDetail(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load thread');
      setOpenThread(null);
    }
  };

  const handleComposeReply = async () => {
    if (!openThread?.thread_id) return;
    setComposeLoading(true);
    try {
      const res = await axios.post(
        `${API}/gmail/threads/${openThread.thread_id}/compose-reply`,
        { instructions: composeInstructions.trim(), tone: composeTone },
        { withCredentials: true }
      );
      setComposeDraft(res.data);
      setComposeOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to draft reply');
    } finally {
      setComposeLoading(false);
    }
  };

  const handleCopyDraft = async () => {
    if (!composeDraft) return;
    const text =
      `To: ${composeDraft.to || ''}\n` +
      `Subject: ${composeDraft.subject || ''}\n\n` +
      `${composeDraft.body || ''}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Draft copied to clipboard');
    } catch {
      toast.error('Could not copy — please select and copy manually');
    }
  };

  const handleOpenInGmail = () => {
    if (!composeDraft) return;
    const params = new URLSearchParams({
      view: 'cm',
      fs: '1',
      to: composeDraft.to || '',
      su: composeDraft.subject || '',
      body: composeDraft.body || '',
    });
    if (composeDraft.from_account) params.append('authuser', composeDraft.from_account);
    window.open(`https://mail.google.com/mail/?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  const sendChat = async (e, presetText) => {
    e?.preventDefault?.();
    const text = (presetText ?? chatInput).trim();
    if (!text || chatSending) return;
    const next = [...chatMessages, { role: 'user', content: text }];
    setChatMessages(next);
    setChatInput('');
    setChatSending(true);
    try {
      const res = await axios.post(
        `${API}/gmail/chat`,
        { messages: next.map((m) => ({ role: m.role, content: m.content })) },
        { withCredentials: true }
      );
      setChatMessages([...next, { role: 'assistant', content: res.data.reply || '' }]);
      if (res.data.model) setChatModel(res.data.model);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Chat failed';
      setChatMessages([...next, { role: 'assistant', content: `**Error:** ${msg}` }]);
    } finally {
      setChatSending(false);
    }
  };

  const handleQuickPrompt = (prompt) => sendChat(null, prompt);

  const taskCounts = useMemo(() => {
    const c = { open: 0, in_progress: 0, closed: 0 };
    for (const t of tasks) c[t.status] = (c[t.status] || 0) + 1;
    return c;
  }, [tasks]);

  return (
    <div className="space-y-6 min-w-0" data-testid="email-hub-page">
      {/* Header */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight flex items-center gap-2">
            <Mail className="h-7 w-7 text-primary" />
            Email Hub
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Connected mailboxes, AI-extracted tasks, and a chat-bot over your team&apos;s inbox.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSyncAll}
            disabled={syncing || accounts.length === 0}
            data-testid="sync-all-button"
          >
            <RefreshCw className={`h-4 w-4 mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing…' : 'Sync now'}
          </Button>
          {isAdmin && (
            <Button size="sm" onClick={() => setConnectOpen(true)} data-testid="connect-gmail-button">
              <Plus className="h-4 w-4 mr-1.5" />
              Connect Gmail
            </Button>
          )}
        </div>
      </div>

      {/* Connected accounts */}
      {accounts.length === 0 ? (
        <Card data-testid="no-accounts-card">
          <CardContent className="py-10 text-center space-y-3">
            <Inbox className="h-10 w-10 text-muted-foreground mx-auto" strokeWidth={1.5} />
            <p className="text-sm text-muted-foreground">
              No Gmail accounts connected yet.
              {isAdmin
                ? ' Click "Connect Gmail" above to add your first shared mailbox.'
                : ' Ask an admin to connect a shared mailbox.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {accounts.map((a) => (
            <Card key={a.id} className="border-border" data-testid={`account-card-${a.id}`}>
              <CardContent className="p-4 flex items-start gap-3">
                <div className="h-9 w-9 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{a.label || a.email}</div>
                  <div className="text-xs text-muted-foreground truncate">{a.email}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Last sync: {fmtDate(a.last_synced_at)}
                  </div>
                  {a.auth_error && (
                    <div className="text-[11px] text-rose-600 mt-1 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" /> Reconnect required
                    </div>
                  )}
                </div>
                {isAdmin && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-destructive"
                    title="Disconnect"
                    onClick={() => handleDisconnect(a.id, a.email)}
                    data-testid={`disconnect-${a.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Tabs: Tasks · Threads · Chat */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="tasks" data-testid="tab-tasks" className="flex items-center gap-1.5">
            <ListChecks className="h-4 w-4" />
            Open Issues
            <Badge variant="outline" className="ml-1 text-[10px] px-1.5 h-4">
              {taskCounts.open || 0}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="threads" data-testid="tab-threads" className="flex items-center gap-1.5">
            <Inbox className="h-4 w-4" />
            Threads
          </TabsTrigger>
          <TabsTrigger value="chat" data-testid="tab-chat" className="flex items-center gap-1.5">
            <MessageSquare className="h-4 w-4" />
            Ask AI
          </TabsTrigger>
        </TabsList>

        {/* TASKS */}
        <TabsContent value="tasks" className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-40">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In progress</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-56">
              <Select value={accountFilter} onValueChange={setAccountFilter}>
                <SelectTrigger data-testid="account-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All accounts</SelectItem>
                  {accounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.label || a.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="default"
              size="sm"
              onClick={() => setAddIssueOpen(true)}
              data-testid="add-open-issue-btn"
              className="ml-auto"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Open Issue
            </Button>
          </div>

          {tasks.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                No tasks match these filters yet. Click <em>Sync now</em> after connecting an
                account to pull and analyse new emails.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {tasks.map((t) => (
                <Card
                  key={t.id}
                  className="border-border hover:shadow-sm transition-shadow"
                  data-testid={`task-${t.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`${PRIORITY_STYLES[t.priority] || ''} border text-[10px] uppercase tracking-wider`}>
                            {t.priority}
                          </Badge>
                          <Badge className={`${STATUS_STYLES[t.status] || ''} border text-[10px] uppercase tracking-wider`}>
                            {t.status.replace('_', ' ')}
                          </Badge>
                          {t.category && (
                            <Badge variant="outline" className="text-[10px]">
                              {t.category.replace('_', ' ')}
                            </Badge>
                          )}
                          {t.due_hint && (
                            <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {t.due_hint}
                            </span>
                          )}
                        </div>
                        <div className="font-semibold mt-2">{t.title}</div>
                        <div className="text-xs text-muted-foreground mt-1">{t.description}</div>
                        <div className="text-[11px] text-muted-foreground mt-2 flex flex-wrap gap-x-3 gap-y-0.5">
                          <span className="inline-flex items-center gap-1">
                            <Badge
                              variant="outline"
                              className={`text-[9px] py-0 h-4 ${
                                t.source === 'manual'
                                  ? 'border-sky-300 text-sky-700 bg-sky-50'
                                  : 'border-violet-300 text-violet-700 bg-violet-50'
                              }`}
                            >
                              {t.source === 'manual' ? 'Manual' : 'AI'}
                            </Badge>
                            Added by: <span className="font-medium text-foreground">{t.created_by_name || (t.source === 'manual' ? 'User' : 'AI')}</span>
                          </span>
                          {t.account_email && <span>From: {t.account_email}</span>}
                          {t.thread_subject && (
                            <button
                              type="button"
                              onClick={() => openThreadFor(t.thread_id)}
                              className="text-primary hover:underline inline-flex items-center gap-1"
                            >
                              Thread: {t.thread_subject}
                              <ExternalLink className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Select
                          value={t.assignee_user_id || 'unassigned'}
                          onValueChange={(v) =>
                            handleTaskUpdate(t, { assignee_user_id: v === 'unassigned' ? '' : v })
                          }
                        >
                          <SelectTrigger className="w-44 h-8 text-xs" data-testid={`assignee-${t.id}`}>
                            <SelectValue placeholder="Assign…" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unassigned">Unassigned</SelectItem>
                            {users.map((u) => (
                              <SelectItem key={u.id} value={u.id}>
                                {u.name || u.email}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={t.status}
                          onValueChange={(v) => handleTaskUpdate(t, { status: v })}
                        >
                          <SelectTrigger className="w-32 h-8 text-xs" data-testid={`status-${t.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="in_progress">In progress</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* THREADS */}
        <TabsContent value="threads" className="space-y-3">
          {threads.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                No email threads yet — sync an account to populate.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {threads.map((th) => (
                <Card
                  key={th.id}
                  className="hover:shadow-sm cursor-pointer transition-shadow"
                  onClick={() => openThreadFor(th.thread_id)}
                  data-testid={`thread-row-${th.thread_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          {th.ai_category && (
                            <Badge variant="outline" className="text-[10px]">
                              {th.ai_category.replace('_', ' ')}
                            </Badge>
                          )}
                          <Badge variant="outline" className="text-[10px]">
                            {th.message_count} msgs
                          </Badge>
                          {th.needs_analysis && (
                            <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-[10px]">
                              Pending analysis
                            </Badge>
                          )}
                        </div>
                        <div className="font-semibold mt-1 truncate">{th.subject}</div>
                        {th.ai_summary && (
                          <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {th.ai_summary}
                          </div>
                        )}
                        <div className="text-[11px] text-muted-foreground mt-2">
                          {th.account_email} · last activity {fmtDate(th.last_message_at)}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* CHAT */}
        <TabsContent value="chat">
          <Card className="h-[60vh] flex flex-col" data-testid="chat-card">
            <CardHeader className="border-b py-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-accent" />
                  <CardTitle className="text-base">Ask anything about your inboxes</CardTitle>
                </div>
                {chatModel && (
                  <Badge variant="outline" className="text-[10px] font-mono" data-testid="chat-model-badge">
                    {chatModel}
                  </Badge>
                )}
              </div>
              <CardDescription className="text-xs">
                The bot reads recent thread summaries and open tasks. Session-only — no chat history is saved.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto p-4 space-y-3">
              {chatMessages.length === 0 ? (
                <div className="text-center py-6 space-y-4" data-testid="chat-empty-state">
                  <div className="text-sm text-muted-foreground">
                    Ask anything about your team&apos;s inboxes — pick a quick prompt or type your own.
                  </div>
                  <div className="flex flex-wrap items-center justify-center gap-2 max-w-2xl mx-auto">
                    {QUICK_PROMPTS.map((q) => {
                      const Icon = q.icon;
                      return (
                        <Button
                          key={q.label}
                          variant="outline"
                          size="sm"
                          className="rounded-full text-xs h-8"
                          disabled={chatSending}
                          onClick={() => handleQuickPrompt(q.prompt)}
                          data-testid={`quick-prompt-${q.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <Icon className="h-3.5 w-3.5 mr-1.5" />
                          {q.label}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                chatMessages.map((m, i) => (
                  <div
                    key={i}
                    className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`rounded-2xl px-4 py-2.5 text-sm ${
                        m.role === 'user'
                          ? 'bg-primary text-primary-foreground max-w-[85%]'
                          : 'bg-muted text-foreground max-w-[92%] w-full'
                      }`}
                      data-testid={`chat-msg-${m.role}-${i}`}
                    >
                      {m.role === 'user' ? (
                        <span className="whitespace-pre-wrap">{m.content}</span>
                      ) : (
                        <MarkdownView>{m.content}</MarkdownView>
                      )}
                    </div>
                  </div>
                ))
              )}
              {chatSending && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-2xl px-4 py-2.5 text-xs text-muted-foreground inline-flex items-center gap-2">
                    <Sparkles className="h-3 w-3 animate-pulse text-accent" />
                    Thinking…
                  </div>
                </div>
              )}
              <div ref={chatBottomRef} />
            </CardContent>
            {chatMessages.length > 0 && (
              <div className="border-t bg-muted/20 px-3 py-2 flex items-center gap-1.5 overflow-x-auto whitespace-nowrap" data-testid="quick-prompt-strip">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1 flex-shrink-0">
                  Quick:
                </span>
                {QUICK_PROMPTS.map((q) => {
                  const Icon = q.icon;
                  return (
                    <Button
                      key={q.label}
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs flex-shrink-0"
                      disabled={chatSending}
                      onClick={() => handleQuickPrompt(q.prompt)}
                      data-testid={`quick-prompt-strip-${q.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <Icon className="h-3 w-3 mr-1" />
                      {q.label}
                    </Button>
                  );
                })}
              </div>
            )}
            <form onSubmit={sendChat} className="border-t p-3 flex items-center gap-2">
              <Input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Ask about your emails…"
                disabled={chatSending}
                data-testid="chat-input"
              />
              <Button type="submit" disabled={chatSending || !chatInput.trim()} data-testid="chat-send">
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Connect Gmail dialog */}
      <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Connect a Gmail account</DialogTitle>
            <DialogDescription>
              You&apos;ll be redirected to Google to authorize read-only access to inbox &amp;
              sent mail. Refresh tokens are encrypted at rest.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="connect-label">Label (optional)</Label>
              <Input
                id="connect-label"
                value={connectLabel}
                onChange={(e) => setConnectLabel(e.target.value)}
                placeholder="e.g. Accounts inbox"
                data-testid="connect-label-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConnectOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConnect}
              disabled={connecting}
              data-testid="connect-submit-button"
            >
              {connecting ? 'Redirecting…' : 'Continue with Google'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Thread detail dialog */}
      <Dialog open={!!openThread} onOpenChange={(v) => !v && setOpenThread(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="truncate">
              {openThreadDetail?.thread?.subject || 'Loading…'}
            </DialogTitle>
            {openThreadDetail?.thread?.ai_summary && (
              <DialogDescription>
                <span className="font-semibold text-foreground">AI summary:</span>{' '}
                {openThreadDetail.thread.ai_summary}
              </DialogDescription>
            )}
          </DialogHeader>
          {openThreadDetail ? (
            <div className="space-y-3">
              {/* Compose AI Reply control */}
              <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-2" data-testid="compose-reply-card">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <Wand2 className="h-4 w-4 text-primary" />
                    Compose AI Reply
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={composeTone} onValueChange={setComposeTone}>
                      <SelectTrigger className="h-8 w-36 text-xs" data-testid="compose-tone">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="friendly">Friendly</SelectItem>
                        <SelectItem value="firm">Firm</SelectItem>
                        <SelectItem value="formal">Formal</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      onClick={handleComposeReply}
                      disabled={composeLoading}
                      data-testid="compose-reply-button"
                    >
                      <Wand2 className={`h-4 w-4 mr-1.5 ${composeLoading ? 'animate-pulse' : ''}`} />
                      {composeLoading ? 'Drafting…' : composeDraft ? 'Re-draft' : 'Draft reply'}
                    </Button>
                  </div>
                </div>
                <Textarea
                  value={composeInstructions}
                  onChange={(e) => setComposeInstructions(e.target.value)}
                  placeholder="Optional: extra instructions, e.g. ‘Confirm payment will be released by Friday and ask for the GST invoice.’"
                  className="text-xs"
                  rows={2}
                  data-testid="compose-instructions"
                />
                {composeOpen && composeDraft && (
                  <div
                    className="rounded-md border border-border bg-background p-3 space-y-2"
                    data-testid="compose-draft-card"
                  >
                    <div className="text-[11px] text-muted-foreground">
                      <span className="font-semibold text-foreground">From:</span> {composeDraft.from_account || '—'}{' '}
                      &nbsp;·&nbsp;
                      <span className="font-semibold text-foreground">To:</span> {composeDraft.to || '—'}
                    </div>
                    <div className="text-xs">
                      <span className="font-semibold">Subject:</span> {composeDraft.subject}
                    </div>
                    <Textarea
                      value={composeDraft.body || ''}
                      onChange={(e) => setComposeDraft({ ...composeDraft, body: e.target.value })}
                      rows={10}
                      className="text-xs font-sans"
                      data-testid="compose-draft-body"
                    />
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCopyDraft}
                        data-testid="compose-copy-button"
                      >
                        <Copy className="h-3.5 w-3.5 mr-1.5" />
                        Copy draft
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleOpenInGmail}
                        data-testid="compose-open-gmail-button"
                      >
                        <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                        Open in Gmail
                      </Button>
                      <span className="text-[10px] text-muted-foreground">
                        Drafted by {composeDraft.model} · tone: {composeDraft.tone}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {(openThreadDetail.tasks || []).length > 0 && (
                <div className="rounded-md border border-border bg-muted/40 p-3">
                  <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                    Tasks from this thread
                  </div>
                  <div className="space-y-1">
                    {openThreadDetail.tasks.map((t) => (
                      <div key={t.id} className="text-xs flex items-center gap-2">
                        <CheckCircle2
                          className={`h-3.5 w-3.5 ${
                            t.status === 'closed' ? 'text-emerald-600' : 'text-muted-foreground'
                          }`}
                        />
                        <span
                          className={
                            t.status === 'closed' ? 'line-through text-muted-foreground' : ''
                          }
                        >
                          {t.title}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-2">
                {(openThreadDetail.messages || []).map((m) => (
                  <div
                    key={m.id}
                    className="rounded-md border border-border bg-background p-3 text-xs"
                  >
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge
                        variant="outline"
                        className="text-[10px] uppercase tracking-wider"
                      >
                        {m.direction}
                      </Badge>
                      <span className="font-semibold">
                        {(m.from || [{}])[0]?.name || (m.from || [{}])[0]?.email || ''}
                      </span>
                      <span className="text-muted-foreground">{fmtDate(m.date)}</span>
                    </div>
                    <pre className="whitespace-pre-wrap font-sans text-xs text-foreground/90">
                      {(m.body_plain || m.snippet || '').slice(0, 4000)}
                    </pre>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-sm text-muted-foreground">Loading thread…</div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Open Issue dialog */}
      <Dialog
        open={addIssueOpen}
        onOpenChange={(v) => {
          setAddIssueOpen(v);
          if (!v) resetNewIssue();
        }}
      >
        <DialogContent className="max-w-lg" data-testid="add-open-issue-dialog">
          <DialogHeader>
            <DialogTitle>Add Open Issue</DialogTitle>
            <DialogDescription>
              Manually log an issue and assign it to a teammate. Your name is recorded as the
              creator.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="issue-title">Title *</Label>
              <Input
                id="issue-title"
                value={newIssue.title}
                onChange={(e) => setNewIssue((s) => ({ ...s, title: e.target.value }))}
                placeholder="e.g. Vendor X invoice mismatch"
                data-testid="issue-title-input"
              />
            </div>
            <div>
              <Label htmlFor="issue-desc">Description</Label>
              <Textarea
                id="issue-desc"
                rows={3}
                value={newIssue.description}
                onChange={(e) => setNewIssue((s) => ({ ...s, description: e.target.value }))}
                placeholder="Optional context, links, what needs to happen…"
                data-testid="issue-desc-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Priority</Label>
                <Select
                  value={newIssue.priority}
                  onValueChange={(v) => setNewIssue((s) => ({ ...s, priority: v }))}
                >
                  <SelectTrigger data-testid="issue-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="issue-due">Due (optional)</Label>
                <Input
                  id="issue-due"
                  value={newIssue.due_hint}
                  onChange={(e) => setNewIssue((s) => ({ ...s, due_hint: e.target.value }))}
                  placeholder="e.g. EOD Fri, 15 May"
                  data-testid="issue-due-input"
                />
              </div>
            </div>
            <div>
              <Label>Assignee</Label>
              <Select
                value={newIssue.assignee_user_id}
                onValueChange={(v) => setNewIssue((s) => ({ ...s, assignee_user_id: v }))}
              >
                <SelectTrigger data-testid="issue-assignee-select">
                  <SelectValue placeholder="Choose a teammate" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name || u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {accounts.length > 0 && (
              <div>
                <Label>Link to Gmail account (optional)</Label>
                <Select
                  value={newIssue.account_id}
                  onValueChange={(v) => setNewIssue((s) => ({ ...s, account_id: v }))}
                >
                  <SelectTrigger data-testid="issue-account-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No account link</SelectItem>
                    {accounts.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.label || a.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddIssueOpen(false)} disabled={addIssueSaving}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateIssue}
              disabled={addIssueSaving || !newIssue.title.trim()}
              data-testid="issue-save-btn"
            >
              {addIssueSaving ? 'Adding…' : 'Add Issue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailHub;
